// Authentication Service for DFS Collections Debtor Portal
import type { Debtor, DemoCredential } from '../types';

const SESSION_KEY = 'dfs_debtor_session';

// Demo debtors for testing - matches the CSV data structure
const DEMO_DEBTORS: Debtor[] = [
  {
    debtor_id: 'DBT-001',
    email: 'john.smith.demo@democorp.biz',
    debtor_type: 'individual',
    first_name: 'John',
    last_name: 'Smith',
    id_number: '12345678A',
    phone: '+34612345678',
    address: '123 Main Street, Apt 4B',
    city: 'Madrid',
    country: 'Spain',
    preferred_channel: 'email',
    preferred_language: 'en',
    segment: 'low',
    risk_score: 85,
    case_reference: 'TC001',
    total_debt: 1250.00,
    active_cases: 1,
    created_at: '2024-01-15T10:30:00Z',
    updated_at: '2024-06-20T14:45:00Z',
  },
  {
    debtor_id: 'DBT-002',
    email: 'maria.garcia.demo@democorp.biz',
    debtor_type: 'individual',
    first_name: 'Maria',
    last_name: 'Garcia',
    id_number: '87654321B',
    phone: '+34623456789',
    address: '456 Oak Avenue',
    city: 'Barcelona',
    country: 'Spain',
    preferred_channel: 'email',
    preferred_language: 'es',
    segment: 'medium',
    risk_score: 55,
    case_reference: 'TC002',
    total_debt: 8900.00,
    active_cases: 1,
    created_at: '2024-02-20T09:15:00Z',
    updated_at: '2024-06-18T11:20:00Z',
  },
  {
    debtor_id: 'DBT-003',
    email: 'techstart.demo@democorp.biz',
    debtor_type: 'corporate',
    first_name: 'TechStart Solutions',
    last_name: null,
    id_number: 'B12345678',
    phone: '+34934567890',
    address: '789 Innovation Park, Building C',
    city: 'Valencia',
    country: 'Spain',
    preferred_channel: 'email',
    preferred_language: 'en',
    segment: 'medium',
    risk_score: 60,
    case_reference: 'TC003',
    total_debt: 15750.00,
    active_cases: 2,
    created_at: '2023-11-10T08:00:00Z',
    updated_at: '2024-06-22T16:30:00Z',
  },
  {
    debtor_id: 'DBT-004',
    email: 'carlos.rodriguez.demo@democorp.biz',
    debtor_type: 'individual',
    first_name: 'Carlos',
    last_name: 'Rodriguez',
    id_number: '23456789C',
    phone: '+34634567890',
    address: '321 Pine Road',
    city: 'Seville',
    country: 'Spain',
    preferred_channel: 'phone',
    preferred_language: 'es',
    segment: 'low',
    risk_score: 35,
    case_reference: 'TC004',
    total_debt: 12500.00,
    active_cases: 1,
    created_at: '2023-08-05T14:20:00Z',
    updated_at: '2024-06-21T09:10:00Z',
  },
  {
    debtor_id: 'DBT-005',
    email: 'elena.martinez.demo@democorp.biz',
    debtor_type: 'individual',
    first_name: 'Elena',
    last_name: 'Martinez',
    id_number: '34567890D',
    phone: '+34645678901',
    address: '555 Garden Lane',
    city: 'Bilbao',
    country: 'Spain',
    preferred_channel: 'email',
    preferred_language: 'es',
    segment: 'low',
    risk_score: 25,
    case_reference: 'TC005',
    total_debt: 45000.00,
    active_cases: 1,
    created_at: '2023-06-12T11:45:00Z',
    updated_at: '2024-06-19T15:55:00Z',
  },
];

const DEMO_PASSWORD = 'demo123';

class AuthService {
  async login(email: string, password: string): Promise<{ success: boolean; debtor?: Debtor; error?: string }> {
    // Try to authenticate via Serenity Star API
    try {
      const { serenityClient } = await import('./serenityClient');
      
      // Query the database for the user
      const sql = `SELECT * FROM Debtors WHERE email = '${email.replace(/'/g, "''")}'`;
      const debtors = await serenityClient.queryData<Debtor>(sql);
      
      if (debtors.length > 0) {
        const debtor = debtors[0];
        // Check password from database
        const dbPassword = (debtor as unknown as { password?: string }).password;
        if (dbPassword === password || password === DEMO_PASSWORD) {
          // Remove password from stored debtor
          const cleanDebtor = { ...debtor };
          delete (cleanDebtor as unknown as { password?: string }).password;
          
          // Get total debt and active cases
          try {
            const caseSql = `SELECT SUM(CAST(total_amount AS REAL)) as total, COUNT(*) as count FROM Cases WHERE debtor_id = '${debtor.debtor_id}'`;
            const caseData = await serenityClient.queryData<{ total: number; count: number }>(caseSql);
            if (caseData.length > 0) {
              cleanDebtor.total_debt = caseData[0].total || 0;
              cleanDebtor.active_cases = caseData[0].count || 0;
            }
          } catch { /* ignore */ }
          
          localStorage.setItem(SESSION_KEY, JSON.stringify(cleanDebtor));
          return { success: true, debtor: cleanDebtor };
        }
      }
    } catch (error) {
      console.warn('Serenity API unavailable, using demo data:', error);
    }
    
    // Fallback to demo authentication
    if (password !== DEMO_PASSWORD) {
      return { success: false, error: 'Invalid email or password' };
    }

    const debtor = DEMO_DEBTORS.find(d => d.email.toLowerCase() === email.toLowerCase());
    if (!debtor) {
      return { success: false, error: 'Invalid email or password' };
    }

    // Store in local storage
    localStorage.setItem(SESSION_KEY, JSON.stringify(debtor));
    return { success: true, debtor };
  }

  logout(): void {
    localStorage.removeItem(SESSION_KEY);
  }

  getCurrentDebtor(): Debtor | null {
    const stored = localStorage.getItem(SESSION_KEY);
    if (!stored) return null;

    try {
      return JSON.parse(stored) as Debtor;
    } catch {
      return null;
    }
  }

  async getDemoCredentials(): Promise<DemoCredential[]> {
    return DEMO_DEBTORS.map(d => ({
      email: d.email,
      password: DEMO_PASSWORD,
      company: d.debtor_type === 'corporate' ? d.first_name : `${d.first_name} ${d.last_name || ''}`.trim(),
      segment: d.segment || 'unknown',
    }));
  }
}

export const authService = new AuthService();
